import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Camera, X, ScanLine, Check, LogIn, LogOut, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import {
  BrutalButton,
  BrutalCard,
  BrutalInput,
  BrutalLabel,
  Badge,
  SectionTitle,
} from "../../components/common/Brutal";

export default function AdminScanner() {
  const [manualId, setManualId] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);

  const stopScanner = async () => {
    try {
      if (html5QrRef.current) {
        await html5QrRef.current.stop();
        await html5QrRef.current.clear();
        html5QrRef.current = null;
      }
    } catch (_) { /* noop */ }
    setScanning(false);
  };

  const startScanner = async () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const qr = new Html5Qrcode("scanner-target");
        html5QrRef.current = qr;
        await qr.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          async (decoded) => {
            await stopScanner();
            await applyScan(decoded, "checked-in");
          },
          () => {}
        );
      } catch (e) {
        toast.error("Camera not available. Use manual entry.");
        setScanning(false);
      }
    }, 50);
  };

  useEffect(() => () => { stopScanner(); }, []);

  const applyScan = async (camperId, newStatus) => {
    try {
      const { data } = await api.post("/qr/scan", {
        camper_id: camperId,
        new_status: newStatus,
      });
      setScanResult(data);
      toast.success(`${data.camper.name} → ${newStatus}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Camper not found");
      setScanResult(null);
    }
  };

  const handleManual = async (status) => {
    if (!manualId.trim()) return toast.error("Enter or scan a code");
    await applyScan(manualId.trim(), status);
  };

  return (
    <div className="space-y-6" data-testid="admin-scanner">
      <SectionTitle kicker="Gate Operations" title="QR Scanner" />

      <div className="grid lg:grid-cols-2 gap-6">
        <BrutalCard className="p-5">
          <h3 className="display text-2xl flex items-center gap-2">
            <Camera className="h-5 w-5" /> Camera Scan
          </h3>
          <div
            id="scanner-target"
            ref={scannerRef}
            className={`mt-4 kf-border bg-black/90 aspect-square w-full max-w-sm mx-auto ${
              scanning ? "" : "grid place-items-center"
            }`}
            data-testid="scanner-target"
          >
            {!scanning && (
              <div className="text-white/70 text-center">
                <ScanLine className="h-12 w-12 mx-auto" />
                <div className="text-xs uppercase tracking-widest mt-2">
                  Camera idle
                </div>
              </div>
            )}
          </div>
          <div className="mt-4 flex gap-2 justify-center">
            {!scanning ? (
              <BrutalButton
                variant="primary"
                onClick={startScanner}
                data-testid="scanner-start"
              >
                <Camera className="h-4 w-4" /> Start Camera
              </BrutalButton>
            ) : (
              <BrutalButton
                variant="dark"
                onClick={stopScanner}
                data-testid="scanner-stop"
              >
                <X className="h-4 w-4" /> Stop
              </BrutalButton>
            )}
          </div>
          <p className="text-xs text-[#6B4E9E] text-center mt-2">
            Scanning auto-marks the camper as <b>Checked-in</b>
          </p>
        </BrutalCard>

        <BrutalCard className="p-5">
          <h3 className="display text-2xl">Manual Entry / Update</h3>
          <p className="text-sm text-[#6B4E9E]">
            Paste a camper ID or QR value to update their status.
          </p>
          <div className="mt-4">
            <BrutalLabel>Camper ID (QR value)</BrutalLabel>
            <BrutalInput
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              placeholder="paste camper UUID..."
              data-testid="manual-camper-id"
            />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <BrutalButton
              variant="primary"
              onClick={() => handleManual("checked-in")}
              data-testid="status-checkin"
              className="px-2 text-xs"
            >
              <LogIn className="h-3 w-3" /> Check-in
            </BrutalButton>
            <BrutalButton
              variant="secondary"
              onClick={() => handleManual("checked-out")}
              data-testid="status-checkout"
              className="px-2 text-xs"
            >
              <LogOut className="h-3 w-3" /> Check-out
            </BrutalButton>
            <BrutalButton
              variant="dark"
              onClick={() => handleManual("overdue")}
              data-testid="status-overdue"
              className="px-2 text-xs"
            >
              <AlertTriangle className="h-3 w-3" /> Overdue
            </BrutalButton>
          </div>

          {scanResult && (
            <div className="kf-border bg-[#FFB400] p-4 mt-5" data-testid="scan-result">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5" />
                <Badge color="orange">UPDATED</Badge>
              </div>
              <div className="display text-2xl mt-2">{scanResult.camper.name}</div>
              <div className="text-sm">
                Zone: <b>{scanResult.zone_name}</b>
              </div>
              <div className="text-sm">
                Status: <Badge color="green">{scanResult.camper.status}</Badge>
              </div>
              <div className="text-xs text-[#3D1E6D]/70 mt-1">
                Last change: {new Date(scanResult.camper.last_status_change).toLocaleString()}
              </div>
            </div>
          )}
        </BrutalCard>
      </div>
    </div>
  );
}
